import React, { useState, useCallback, useEffect } from 'react';
import { Box, Typography, IconButton, Button, Dialog } from '@mui/material';
import { withRouter } from 'react-router-dom'
import { i18n } from '../../../../../translate/i18n';
import { handlePropsStage } from './card.style'
import EquipmentInfo from '../../../../EquipmentInfo';
import Chip from '../../../../Chip';
import EditRounded from '@mui/icons-material/EditRounded';
import moment from 'moment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ScheduleServiceModal from '../../../../ScheduleServiceModal';
import { checkFeatureFlag } from '../../../../../utils/checkFeatureFlag';
import { useDispatch, useSelector } from 'react-redux';
import EFeatureFlags from '../../../../../utils/EFeatureFlags';
import * as actions from '../../../../../store/actions';
import { EAccessRole } from '../../../../../store/types';

const backgroundColor = 'rgba(76, 175, 80, 0.3)';
const color = 'rgba(76, 175, 80, 1)';

const EquipServiceCard = props => {
  const dispatch = useDispatch();

  const [scheduledServiceModalOpen, setScheduledServiceModalOpen] = useState(false);

  const filteredUsers = useSelector(state => state.calendars.filteredUsers);
  const filteredUsersMaintenances = useSelector(state => state.calendars.filteredUsersMaintenances);
  const filteredEquipmentModels = useSelector(state => state.calendars.filteredEquipmentModels);
  const filteredEquipmentGroups = useSelector(state => state.calendars.filteredEquipmentGroups);
  const filteredServiceTypes = useSelector(state => state.calendars.filteredServiceTypes);
  const filteredFrequencies = useSelector(state => state.calendars.filteredFrequencies);
  const filteredPlaces = useSelector(state => state.calendars.filteredPlaces);
  const filteredModules = useSelector(state => state.calendars.filteredModules);
  const filteredAreas = useSelector(state => state.calendars.filteredAreas);
  const filteredStatus = useSelector(state => state.calendars.filteredStatus);
  const filteredStatusMaintenances = useSelector(state => state.calendars.filteredStatusMaintenances);
  const filteredCompanies = useSelector(state => state.calendars.filteredCompanies);
  const calendarDate = useSelector(state => state.calendars.calendarDate);
  const selectedSites = useSelector(state => state.sites.selectedSitesPlaces);
  const modalDate = useSelector(state => state.calendars.modalDate);
  const selectedAccess = useSelector(state => state.auth.selectedAccess);

  const isAnalyst = selectedAccess.role === EAccessRole.analyst;
  const isContractor = selectedAccess.role === EAccessRole.contractor;
  const isInspector = selectedAccess.inspector && (isAnalyst || isContractor);
  
  const limitToShowAlreadyShowingData = 20 * props.page;

  const fetchInProgressServicesByDay = useCallback(() => dispatch(actions.fetchInProgressServicesByDay(1, limitToShowAlreadyShowingData, modalDate, selectedSites, filteredUsers, filteredUsersMaintenances, filteredEquipmentModels, filteredEquipmentGroups, filteredModules, filteredAreas, filteredCompanies, filteredPlaces, filteredServiceTypes, filteredFrequencies, filteredStatus, filteredStatusMaintenances, true)));

  const tenant = useSelector((state) => state.auth.selectedAccess.tenant);

  const equipment = props.data;

  const serviceName = `${equipment.equipmentServicesReplicate.equipmentServiceId.serviceType.name} ${equipment.equipmentServicesReplicate.equipmentServiceId.frequency.name}`;

  const scheduledIndex = equipment?.scheduledServicesReplicated.findIndex((scheduledService) => {
    const hasSameId = scheduledService.equipmentServiceId === equipment.equipmentServicesReplicate.equipmentServiceId._id;
    return (hasSameId && scheduledService.scheduled);
  });

  const scheduledPopulatedIndex = equipment?.scheduledServicesReplicatedPopulated.findIndex((scheduledService) => {
    const hasSameId = scheduledService?.equipmentServiceId === equipment.equipmentServicesReplicate.equipmentServiceId._id;
    return hasSameId;
  });

  const InfoWithLabels = ({ label, info }) => {
    return <Box>
      <Typography sx={styles.label}>{label}</Typography>
      <Typography sx={styles.info}>{info}</Typography>
    </Box>
  };

  const hasScheduledServicesFeatureFlag = checkFeatureFlag({ featureFlags: tenant.featureFlags, featureFlagToCheck: EFeatureFlags.SCHEDULED_SERVICES });

  return (
    <>
      <Box style={ {...styles.container, borderLeft: `${handlePropsStage(props.stage)} 4px solid` } } stage={props.stage}>
        { (hasScheduledServicesFeatureFlag && !isInspector) ?
          <>
            { scheduledIndex > -1 && <Chip backgroundColor={backgroundColor} color={color} label={i18n.t('calendarServiceCard.scheduled')} sxBox={{ marginBottom: '12px', width: '100px', display: 'flex', justifyContent: 'center', alignItems: 'center'}}/> }
            <EquipmentInfo equipment={equipment} serviceName={serviceName} backPage='calendar' />
            { scheduledIndex > -1 ?
              <Box sx={{backgroundColor: 'rgba(46, 125, 50, 0.08)', borderRadius: '5px', padding: '12px 8px', marginTop: '5px'}}>
                <Box sx={{width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                  <InfoWithLabels label={i18n.t('calendarServiceCard.scheduledTo')} info={equipment?.scheduledServicesReplicated[scheduledIndex].scheduledServiceAccountablesNames} />
                  <IconButton children={<EditRounded fontSize='small' sx={{color}}/>} disabled={false} onClick={() => setScheduledServiceModalOpen(true)} color={color}/>
                </Box>
                <InfoWithLabels label={i18n.t('calendarServiceCard.scheduledDate')} info={moment(equipment?.scheduledServicesReplicated[scheduledIndex].scheduledDate).format('DD/MM/yyyy')} />
              </Box>
              :            
              <Button
                variant='outlined'
                size='small'
                sx={{alignSelf: 'flex-end', maxWidth: '150px', marginTop: '12px', color, padding: '3px 7px', borderRadius: '8px'}}
                startIcon={<CalendarTodayIcon />}
                onClick={() => setScheduledServiceModalOpen(true)}
              >
                {i18n.t('calendarServiceCard.schedule')}
              </Button>
            }
          </>
          :
          <EquipmentInfo equipment={equipment} serviceName={serviceName} backPage='calendar' />
        }
      </Box>
      <Dialog
        onClose={() => setScheduledServiceModalOpen(false)}
        open={scheduledServiceModalOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <ScheduleServiceModal
          equipment={equipment}
          equipmentServicesReplicate={equipment.equipmentServicesReplicate}
          backPage='calendar'
          serviceName={serviceName}
          scheduledDate={scheduledIndex > -1 ? equipment?.scheduledServicesReplicated[scheduledIndex].scheduledDate : null}
          accountableId={scheduledPopulatedIndex > -1 ? equipment?.scheduledServicesReplicatedPopulated[scheduledPopulatedIndex].accountables[0] : ""}
          scheduledServiceId={scheduledIndex > -1 ? equipment?.scheduledServicesReplicated[scheduledIndex].scheduledServiceId : ""}
          closeModal={() => setScheduledServiceModalOpen(false)}
          fetchData={() => {fetchInProgressServicesByDay()}}
        />

      </Dialog>
    </>
  );
}

const styles = {
  label: {
    color,
    fontSize: 12,
    fontWeight: '400',
    letterSpacing: 0.15,
    wordWrap: 'break-word'
  },
  info: {
    color,
    fontSize: 14,
    fontWeight: '600',
    letterSpacing: 0.15,
    wordWrap: 'break-word'
  },
  container: {
    borderRadius: 5,
    padding: '8px 12px',
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '16px',
  },
}


export default withRouter(EquipServiceCard);

